import React,{Suspense,useEffect,useState} from 'react';
import {QueryClient,QueryClientProvider} from '@tanstack/react-query';
import { BrowserRouter,Navigate,Route,Routes } from 'react-router-dom';
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import RouteLoadingFallback from './components/RouteLoadingFallback';
import PlanFeatureRoute from './components/billing/PlanFeatureRoute';
import WelcomeToastBridge from './components/WelcomeToastBridge';
import ScrollToTop from './components/ScrollToTop';
import NativeAuthBridge from './components/auth/NativeAuthBridge';
import AppleTransactionBridge from './components/billing/AppleTransactionBridge';
import {OfflineQueueProvider} from './contexts/OfflineQueueContext';
import {BusinessProvider} from './contexts/BusinessContext';
import {useLanguage} from './contexts/LanguageContext';
import {PwaInstallProvider} from './contexts/PwaInstallContext';
import {isSupabaseConfigured,supabase} from './lib/supabase/client';
import {getCurrentUserProfile} from './lib/supabase/profile';
import './App.css';
import {legacyInvoiceRedirect} from './lib/invoices/publicInvoiceLinks';
import {
  AccountantAccessPage, AccountantInvitePage, AccountantRequestsPage, AccountDeletionPage, AuthConfirmPage, BankingPage, CashFlowPage,
  BillingPage, BlogEditorPage, BusinessCreationPage, BusinessSettingsPage, ContractorInvitePage, ContractorInvoicesPage,
  ContractorPaymentPage, ContractorPortalPage, ContractorSubmissionsPage, ContractorsPage, ExpenseFormPage, ExpensesPage,
  FeatureRequestsPage, ForgotPasswordPage, IncomeFormPage, IncomePage,
  InvoiceBuilderPage, InvoicesList, LoginPage, NeedsAttentionPage, OnboardingPage, PaymentMethodsPage,
  ProfilePage, ProductionHealthPage, PublicInvoicePage, ReceiptInboxPage, RecurringInvoicesPage, MerchantRulesPage,
  RemindersPage, RepaymentsPage, ResetPasswordPage, ReviewPage, SignupPage,
  SupportPage, TaxCentrePage, TripFormPage, TripsPage, VehiclesPage, WorkspaceHome
} from './routes/lazyPages';

const legacyInvoicePath = legacyInvoiceRedirect(window.location.hash);
if (legacyInvoicePath) window.history.replaceState(null, '', legacyInvoicePath);

const queryClient=new QueryClient();

function App() {
  const {setLanguage}=useLanguage();
  const [session,setSession]=useState(null);
  const [ready,setReady]=useState(!isSupabaseConfigured);

  useEffect(()=> {
    if (!supabase) {
      setReady(true);
      return undefined;
    }

    let mounted=true;

    const loadSession=async ()=> {
      const {data,error}=await supabase.auth.getSession();

      if (error && import.meta.env.DEV) {
        console.error('Supabase session check failed:',error);
      }

      if (!mounted) {
        return;
      }

      setSession(data.session);
      const initialLocale = data.session?.user?.user_metadata?.interface_locale;
      if (initialLocale) {
        setLanguage(initialLocale);
      }
      setReady(true);

      if (data.session) {
        await getCurrentUserProfile();
      }
    };

    loadSession();

    const {data}=supabase.auth.onAuthStateChange((event,nextSession)=> {
      if (!mounted) {
        return;
      }

      setSession(nextSession);
      const nextLocale = nextSession?.user?.user_metadata?.interface_locale;
      if (nextLocale) {
        setLanguage(nextLocale);
      }
      setReady(true);
    });

    return ()=> {
      mounted=false;
      data.subscription.unsubscribe();
    };
  },[setLanguage]);

  return (
    <PwaInstallProvider>
    <QueryClientProvider client={queryClient}>
      <OfflineQueueProvider session={session}>
      <BusinessProvider>
        <BrowserRouter>
          <NativeAuthBridge />
          <AppleTransactionBridge session={session} />
          <ScrollToTop />
          <WelcomeToastBridge />
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/invoice/public" element={<PublicInvoicePage />} />
            <Route path="/invoice/public/:token" element={<PublicInvoicePage />} />
            <Route path="/login" element={<LoginPage session={session} />} />
            <Route path="/signup" element={<SignupPage session={session} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/confirm" element={<AuthConfirmPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route element={<ProtectedRoute session={session} ready={ready} />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/accountant/invite/:invitationId" element={<AccountantInvitePage />} />
              <Route path="/contractor/invite/:token" element={<ContractorInvitePage />} />
              <Route path="/contractor/submissions" element={<ContractorSubmissionsPage />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route element={<DashboardLayout />}>
                <Route path="/" element={<WorkspaceHome />} />
                <Route path="/trips" element={<TripsPage />} />
                <Route path="/trips/new" element={<TripFormPage />} />
                <Route path="/vehicles" element={<VehiclesPage />} />
                <Route path="/businesses/new" element={<BusinessCreationPage />} />
                <Route path="/settings/profile" element={<ProfilePage />} />
                <Route path="/settings/delete-account" element={<AccountDeletionPage />} />
                <Route path="/settings/blog" element={<BlogEditorPage />} />
                <Route path="/settings/health" element={<ProductionHealthPage />} />
                <Route path="/settings/billing" element={<BillingPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/features" element={<FeatureRequestsPage />} />
                <Route path="/settings" element={<BusinessSettingsPage />} />
                <Route path="/settings/business" element={<BusinessSettingsPage />} />
                <Route element={<PlanFeatureRoute />}>
                  <Route path="/attention" element={<NeedsAttentionPage />} />
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/receipt-inbox" element={<ReceiptInboxPage />} />
                  <Route path="/reminders" element={<RemindersPage />} />
                  <Route path="/accountant/requests" element={<AccountantRequestsPage />} />
                  <Route path="/banking" element={<BankingPage />} />
                  <Route path="/cash-flow" element={<CashFlowPage />} />
                  <Route path="/expenses/new" element={<ExpenseFormPage />} />
                  <Route path="/income" element={<IncomePage />} />
                  <Route path="/income/new" element={<IncomeFormPage />} />
                  <Route path="/invoices" element={<InvoicesList />} />
                  <Route path="/invoices/recurring" element={<RecurringInvoicesPage />} />
                  <Route path="/invoices/new" element={<InvoiceBuilderPage />} />
                  <Route path="/invoices/:invoiceId/edit" element={<InvoiceBuilderPage />} />
                  <Route path="/contractors" element={<ContractorsPage />} />
                  <Route path="/contractors/invoices" element={<ContractorInvoicesPage />} />
                  <Route path="/contractors/portal" element={<ContractorPortalPage />} />
                  <Route path="/contractors/invoices/:invoiceId" element={<ContractorPaymentPage />} />
                  <Route path="/contractors/invoices/:invoiceId/payment" element={<ContractorPaymentPage />} />
                  <Route path="/tax" element={<TaxCentrePage />} />
                  <Route path="/repayments" element={<RepaymentsPage />} />
                  <Route path="/review" element={<ReviewPage />} />
                  <Route path="/settings/payment-methods" element={<PaymentMethodsPage />} />
                  <Route path="/settings/merchant-rules" element={<MerchantRulesPage />} />
                  <Route path="/settings/accountants" element={<AccountantAccessPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </BusinessProvider>
      </OfflineQueueProvider>
    </QueryClientProvider>
    </PwaInstallProvider>
  );
}

export default App;

